/**
 * BE Position is `{ id, person }`. Older FE shapes had Person fields on the root.
 * Helpers normalize both so badges/links don't scatter `?.person` everywhere.
 *
 * Vacant seats: with enablePositionBackedVacancies, BE nulls GraphQL `personId`
 * / `person` when storage has person_id = position_id. Legacy payloads may still
 * expose personId === positionId.
 *
 * Customer-facing positionCode is preferred when present (not on schema yet).
 */

import moment, { type Moment } from "moment";

import { formatRequisitionState } from "./requisitionState";

export type PositionPersonLike = {
  aggregateId?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  avatar?: string | null;
  isVacancy?: boolean | null;
  positionCode?: string | null;
  [key: string]: unknown;
};

export type PositionLike = {
  id?: string | null;
  /** Position.name — vacancy preferredName, not preferredName + surname. */
  name?: string | null;
  /** Establishment FTE: the seat's own size (Position.person.fte), not the occupant sum. */
  establishedFte?: number | null;
  occupancyStatus?: PositionOccupancyStatus | null;
  positionCode?: string | null;
  person?: PositionPersonLike | null;
  aggregateId?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  surname?: string | null;
  [key: string]: unknown;
};

export type PersonWithPositionLike = {
  aggregateId?: string | null;
  preferredName?: string | null;
  firstName?: string | null;
  isVacancy?: boolean | null;
  positionCode?: string | null;
  /** On a vacancy person this is the establishment FTE (Position.person.fte). */
  fte?: number | null;
  filledPositions?: Array<PositionLike | null> | null;
  /**
   * Live requisitions claiming this seat, attached client-side from
   * searchRequisitions. A seat can carry more than one (job share), so this is
   * a list; the first is what the row shows.
   */
  openRequisitions?: Array<{
    id?: string | null;
    state?: string | null;
    fte?: number | null;
  } | null> | null;
};

type MembershipLike = {
  personId?: string | null;
  positionId?: string | null;
};

/**
 * BE defaults searchMemberships to ALLOCATION-only unless positionIds is set.
 * Supply seats are POSITION_SEAT, so team/person list queries must opt in when
 * the flag is on or vacant seats never appear.
 */
export const POSITION_BACKED_MEMBERSHIP_TYPES = [
  "ALLOCATION",
  "POSITION_SEAT",
] as const;

export type PositionBackedMembershipType =
  (typeof POSITION_BACKED_MEMBERSHIP_TYPES)[number];

/** membershipTypes for list searches that must include vacant seats. */
export function membershipTypesForPositionBackedSearch(
  enablePositionBackedVacancies: boolean | null | undefined
): PositionBackedMembershipType[] | undefined {
  return enablePositionBackedVacancies
    ? [...POSITION_BACKED_MEMBERSHIP_TYPES]
    : undefined;
}

export function getPositionPerson(
  position: PositionLike | null | undefined
): PositionPersonLike | null {
  if (!position) {
    return null;
  }
  if (position.person) {
    return position.person;
  }
  return position as PositionPersonLike;
}

/** Position.name / vacancy preferredName — never surname (getName concatenates it). */
export function getPositionDisplayName(
  position: PositionLike | null | undefined
): string | null {
  const fromPosition =
    typeof position?.name === "string" ? position.name.trim() : "";
  if (fromPosition) {
    return fromPosition;
  }
  const person = getPositionPerson(position);
  return person?.preferredName?.trim() || person?.firstName?.trim() || null;
}

export function getPositionId(
  position: PositionLike | null | undefined
): string | null {
  if (!position) {
    return null;
  }
  return (
    position.id ?? position.person?.aggregateId ?? position.aggregateId ?? null
  );
}

/** Customer code wins when set; otherwise TeamForm position id. */
export function getDisplayPositionId(
  position: PositionLike | null | undefined
): string | null {
  if (!position) {
    return null;
  }
  const customerCode =
    position.positionCode?.trim() ||
    position.person?.positionCode?.trim() ||
    null;
  if (customerCode) {
    return customerCode;
  }
  return getPositionId(position)?.trim() || null;
}

export type ForecastMemberPositionKind = "filled" | "open" | "requisition";

/**
 * Server occupancy, when the caller has it. `isVacancy` only says "this person is
 * a position", never whether anyone occupies it, so a filled position used to
 * render as "Open position". Where the demand-request read supplies
 * `occupancyStatus` (teamform-api #7613), it decides instead.
 *
 * VACANT / PARTIALLY_FILLED still have something to hire, so they stay open;
 * FILLED and OVER_FILLED do not.
 */
const isOpenOccupancy = (
  occupancyStatus: PositionOccupancyStatus | null | undefined
): boolean =>
  occupancyStatus === "VACANT" || occupancyStatus === "PARTIALLY_FILLED";

export type ForecastMemberPositionInfo = {
  /** TeamForm position id (always when known). */
  positionId: string;
  /** Label for the badge — customer code preferred. */
  displayId: string;
  kind: ForecastMemberPositionKind;
  requisitionId?: string | null;
  requisitionStatus?: string | null;
};

/**
 * Resolve the position decoration for a forecast/DR member person.
 * Open seat: isVacancy person. Requisition: vacancy + requisition attrs.
 * Filled: first filledPositions entry.
 *
 * Pass `occupancyStatus` from the demand-request member entry where it is
 * available: it is authoritative about occupancy, which `isVacancy` cannot be.
 * Omitting it keeps the previous isVacancy-only behaviour, so callers without a
 * server status (person profiles, team pages) are unaffected.
 */
export function getForecastMemberPositionInfo(
  person: PersonWithPositionLike | null | undefined,
  {
    occupancyStatus,
    positionId: serverPositionId,
  }: {
    occupancyStatus?: PositionOccupancyStatus | null | undefined;
    positionId?: string | null | undefined;
  } = {}
): ForecastMemberPositionInfo | null {
  if (!person) {
    return null;
  }

  if (person.isVacancy) {
    // A position the server calls filled is not an open seat, whatever
    // isVacancy says. Report it as filled against its own id — there is no
    // filledPositions entry on the position person itself.
    if (occupancyStatus && !isOpenOccupancy(occupancyStatus)) {
      const filledPositionId = person.aggregateId?.trim();
      if (!filledPositionId) {
        return null;
      }
      return {
        positionId: filledPositionId,
        displayId: person.positionCode?.trim() || filledPositionId,
        kind: "filled",
      };
    }

    const positionId = person.aggregateId?.trim();
    if (!positionId) {
      return null;
    }
    const displayId = person.positionCode?.trim() || positionId;
    // A seat is a requisition iff it has a live openRequisition with an id.
    const live = (person.openRequisitions ?? []).find((r) => r?.id?.trim());
    const liveReqId = live?.id?.trim() || null;
    if (liveReqId) {
      return {
        positionId,
        displayId,
        kind: "requisition",
        requisitionId: liveReqId,
        requisitionStatus: formatRequisitionState(live?.state) || null,
      };
    }
    return {
      positionId,
      displayId,
      kind: "open",
    };
  }

  const filled = person.filledPositions?.[0];
  const positionId = getPositionId(filled)?.trim();
  if (!positionId || !filled) {
    // No filledPositions on this person. Demand-request members are one such
    // path — that query never selects the field — so fall back to the position
    // the server attributed to this entry. Without it an occupant carries no
    // position decoration at all and cannot be grouped under its position.
    const attributedPositionId = serverPositionId?.trim();
    if (!attributedPositionId) {
      return null;
    }
    return {
      positionId: attributedPositionId,
      displayId: person.positionCode?.trim() || attributedPositionId,
      kind: "filled",
    };
  }
  const displayId = getDisplayPositionId(filled) || positionId;
  return { positionId, displayId, kind: "filled" };
}

/** Matches the API's DEFAULT_FTE_DECIMAL so client math agrees with capacity errors. */
const FTE_DECIMAL = 2;

const roundFte = (value: number): number => {
  const factor = 10 ** FTE_DECIMAL;
  return Math.round(value * factor) / factor;
};

/**
 * Sum of live requisition claims on a seat, from the client-attached
 * openRequisitions. Null-FTE requisitions claim 0, matching the backend's
 * capacity sum.
 */
export function getSeatRequisitionClaimedFte(
  person: PersonWithPositionLike | null | undefined
): number {
  const claims = (person?.openRequisitions ?? []).filter((requisition) =>
    requisition?.id?.trim()
  );
  return roundFte(
    claims.reduce((sum, requisition) => {
      const fte = Number(requisition?.fte ?? 0);
      return sum + (Number.isFinite(fte) ? fte : 0);
    }, 0)
  );
}

/**
 * FTE still open to claim on a seat: the seat's basis minus live requisition
 * claims. The basis is the caller's seat FTE when known (a demand-request
 * member's open ask, which already nets off fills) falling back to the vacancy
 * person's establishment FTE. Null when neither is known — the server capacity
 * check is authoritative and callers should not guess.
 */
export function getRemainingSeatFte(
  person: PersonWithPositionLike | null | undefined,
  seatFte?: number | null
): number | null {
  const basis =
    typeof seatFte === "number" && Number.isFinite(seatFte)
      ? seatFte
      : typeof person?.fte === "number" && Number.isFinite(person.fte)
        ? person.fte
        : null;
  if (basis == null) {
    return null;
  }
  return Math.max(0, roundFte(basis - getSeatRequisitionClaimedFte(person)));
}

export function isVacantPositionSeat(
  membership: MembershipLike | null | undefined
): boolean {
  if (!membership?.positionId) {
    return false;
  }
  const { personId, positionId } = membership;
  return !personId || personId === positionId;
}

export function isFilledPositionSeat(
  membership: MembershipLike | null | undefined
): boolean {
  return Boolean(
    membership?.positionId &&
    membership.personId &&
    membership.personId !== membership.positionId
  );
}

/**
 * Four-way occupancy from the API (Position.occupancyStatus), derived from
 * establishedFte / filledFte at PIT. Prefer this over local VACANT/FILLED
 * derivation wherever Position is queried.
 */
export type PositionOccupancyStatus =
  | "VACANT"
  | "PARTIALLY_FILLED"
  | "FILLED"
  | "OVER_FILLED";

/** Display label for a four-way occupancy status (matches Positions registry). */
export function getPositionOccupancyStatusLabel(
  occupancyStatus: PositionOccupancyStatus | null | undefined
): string {
  switch (occupancyStatus) {
    case "VACANT":
      return "Vacant";
    case "PARTIALLY_FILLED":
      return "Partially filled";
    case "FILLED":
      return "Filled";
    case "OVER_FILLED":
      return "Over-filled";
    case null:
    case undefined:
      return "Vacant";
    default: {
      const _exhaustive: never = occupancyStatus;
      return _exhaustive;
    }
  }
}

/**
 * StatusDot / RegistryStatus variant for a four-way occupancy status.
 * Same mapping as PositionsRegistry columns (not Badge pills).
 */
export function getPositionOccupancyStatusDotVariant(
  occupancyStatus: PositionOccupancyStatus | null | undefined
): "default" | "warning" | "success" | "danger" {
  switch (occupancyStatus) {
    case "VACANT":
      return "default";
    case "PARTIALLY_FILLED":
      return "warning";
    case "FILLED":
      return "success";
    case "OVER_FILLED":
      return "danger";
    case null:
    case undefined:
      return "default";
    default: {
      const _exhaustive: never = occupancyStatus;
      return _exhaustive;
    }
  }
}

/** Narrow unknown occupancy from GraphQL / card data. */
export function asPositionOccupancyStatus(
  value: unknown
): PositionOccupancyStatus | undefined {
  switch (value) {
    case "VACANT":
    case "PARTIALLY_FILLED":
    case "FILLED":
    case "OVER_FILLED":
      return value;
    default:
      return undefined;
  }
}

/** Fallback when workspace `vacantRoleLabel` is empty. */
export const DEFAULT_POSITION_LABEL = "Position";

/** Label for a position/vacancy person — prefers workspace vacantRoleLabel. */
export function getPositionEntityLabel(
  vacantRoleLabel?: string | null
): string {
  const trimmed = vacantRoleLabel?.trim();
  return trimmed || DEFAULT_POSITION_LABEL;
}

/** Badge / aria text: "Position", "Position: Name", or with id suffix. */
export function formatPositionBadgeLabel(
  vacantRoleLabel?: string | null,
  positionName?: string | null,
  positionId?: string | null
): string {
  const label = getPositionEntityLabel(vacantRoleLabel);
  const name = positionName?.trim() || null;
  const id = positionId?.trim() || null;
  if (name && id) {
    return `${label}: ${name} (${id})`;
  }
  if (name) {
    return `${label}: ${name}`;
  }
  if (id) {
    return `${label}: ${id}`;
  }
  return label;
}

type DateLike = string | Date | Moment | null | undefined;

type DatedSpan = {
  startDate?: DateLike;
  endDate?: DateLike;
};

export type PositionSeatDateRange = {
  startDate: Moment;
  /** Null = ongoing (no end). */
  endDate: Moment | null;
};

/**
 * Position lifetime is not a field on Position — it's the union of vacant-seat
 * and fill membership windows tagged with the position id.
 */
export function getPositionSeatDateRange(
  memberships: DatedSpan[] | null | undefined
): PositionSeatDateRange | null {
  if (!memberships?.length) {
    return null;
  }

  let start: Moment | null = null;
  let end: Moment | null = null;
  let hasOpenEnd = false;

  for (const membership of memberships) {
    if (!membership.startDate) {
      continue;
    }
    const spanStart = moment(membership.startDate);
    start = start ? moment.min(start, spanStart) : spanStart;

    if (!membership.endDate) {
      hasOpenEnd = true;
      continue;
    }
    const spanEnd = moment(membership.endDate);
    end = end ? moment.max(end, spanEnd) : spanEnd;
  }

  if (!start) {
    return null;
  }

  return { startDate: start, endDate: hasOpenEnd ? null : end };
}

export function formatPositionSeatDateRange(
  range: PositionSeatDateRange | null | undefined,
  dateFormat: string
): string | null {
  if (!range) {
    return null;
  }
  const start = range.startDate.format(dateFormat);
  const end = range.endDate ? range.endDate.format(dateFormat) : "Ongoing";
  return `${start} – ${end}`;
}

/** City/state/country from a vacancy person or Position.person facade. */
export function formatPositionLocation(
  entity:
    | {
        city?: string | null;
        state?: string | null;
        country?: string | null;
      }
    | null
    | undefined
): string | null {
  const label = [entity?.city, entity?.state, entity?.country]
    .filter(Boolean)
    .join(", ");
  return label || null;
}
